import React, {
  CSSProperties,
  ReactElement,
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from 'react';
import classNames from 'classnames';
import {
  ThemeName,
  CloseIcon,
  ChevronLeftIcon,
  Button,
  Theme,
  Heading,
} from '@taopix/taopix-design-system';
import {
  buttonStyles,
  buttonSizeTypes,
  ButtonProps,
} from '@taopix/taopix-design-system/dist/types/Components/Button/Button';
import {useUiState} from '../Context/UserInterface/UserInterfaceContext';
import {UserInterfaceActions} from '../Context/UserInterface/UserInterfaceActionTypes';
import {ToolPaletteProps} from './ToolPalette';
import {useExperience} from '../Experience/ExperienceProvider';
import './ToolPaletteSection.scss';
import {useTranslation} from 'react-i18next';

export type ToolPaletteSectionProps = {
  children?: any;
  title: string;
  subSection?: boolean;
  parentselected?: boolean;
  sectionKey: string;
  buttonLabel?: string;
  buttonIcon?: ReactElement;
  buttonClasses?: string;
  buttonStyle?: buttonStyles;
  buttonSize?: buttonSizeTypes;
  showButton?: boolean;
  showCloseButton?: boolean;
  onToggle?: (section: string, open?: boolean) => void;
  customCloseFunction?: () => void;
  showSeparator?: boolean;
  sectionAttributes?: object;
  buttonLayout?: 'auto' | 'stacked' | 'tab' | 'horizontal' | 'icon-only';
  badgeContent?: string;
  smallScreenPanelHeight?: 'auto' | 'full' | 'content';
  subSectionPanelType?: 'pop-up' | 'slide-in';
  disabled?: boolean;
  useFocusMode?: boolean;
} & Pick<ToolPaletteProps, 'toolPaletteStyle'> &
  React.HTMLAttributes<HTMLHeadingElement>;

const ToolPaletteSection = React.forwardRef(
  (
    {
      children,
      title,
      subSection,
      onToggle,
      customCloseFunction,
      parentselected,
      sectionKey,
      buttonLabel,
      buttonIcon,
      buttonClasses,
      showButton = true,
      showCloseButton = true,
      showSeparator = true,
      sectionAttributes = [],
      buttonLayout = 'auto',
      buttonStyle,
      buttonSize,
      badgeContent,
      smallScreenPanelHeight = 'auto',
      subSectionPanelType = 'pop-up',
      disabled,
      useFocusMode,
      toolPaletteStyle = 'docked',
      ...props
    }: ToolPaletteSectionProps,
    ref: React.ForwardedRef<HTMLButtonElement>
  ) => {
    const ui = useUiState();
    const selectedPath = ui.state.palette.section;
    const selected = selectedPath?.includes(sectionKey) ?? false;
    const headingText = title;
    const ariaHidden = selected ? false : true;
    const enabledIfParentSectionselected = !subSection || parentselected ? 0 : -1;
    const enabledIfThisSectionselected = !subSection || selected ? 0 : -1;
    const [isActiveSection, setisActiveSection] = useState<boolean>(false);
    const panelRef = useRef<HTMLDivElement | null>(null);
    const isDragging = useRef(false);
    const startY = useRef(0);
    const currentY = useRef(0);
    const panelYPercent = useRef(0);
    const panelHeight = useRef(0);
    const paletteSectionRef = useRef<HTMLElement>(null);
    const {experience} = useExperience();
    const isOpen = useRef(false);
    const {t} = useTranslation(['toolpalette']);
    const wasSelected = useRef(selected);
    const hasOpenSubsection = useMemo(() => {
      return selectedPath?.includes(sectionKey) && selectedPath !== sectionKey;
    }, [selectedPath, sectionKey]);

    // If a ref is provided, use it; otherwise, create a local one
    const localButtonRef = useRef<HTMLButtonElement>(null);
    const sectionButtonRef = (ref as React.RefObject<HTMLButtonElement>) || localButtonRef;

    const handleTouchStart = (e: React.TouchEvent) => {
      if (isActiveSection) {
        isDragging.current = true;
        startY.current = e.touches[0].clientY;
        // Store the panel height to use in the movement calculations
        const panelTop = e.currentTarget.getClientRects()[0].top;
        panelHeight.current = window.innerHeight - panelTop;
        if (panelRef.current) {
          // Temporarily stop the transform from animating so it responds to the drag properly
          panelRef.current.style.transition = '';
        }
      }
    };

    const handleTouchMove = (e: React.TouchEvent) => {
      if (isActiveSection) {
        if (isDragging.current && panelRef.current) {
          // The panel is positioned using a percentage of its own height (i.e. y = -100% when open)
          // So to close it, convert the touch movement to a percentage of the panelHeight and increase its y position by that percentage
          currentY.current = e.touches[0].clientY;
          const distanceMoved = currentY.current - startY.current;
          panelYPercent.current = -100 + (distanceMoved / panelHeight.current) * 100;
          // panelRef.current.style.transform = `translateY(max(-100%,${panelYPercent.current}%))`;
          panelRef.current.style.setProperty(
            '--panelYPercent',
            `max(-100%,${panelYPercent.current}%)`
          );
        }
      }
    };

    const handleTouchEnd = () => {
      if (isActiveSection) {
        isDragging.current = false;
        if (panelRef.current) {
          // Reinstate the transition
          panelRef.current.style.transition = 'transform 300ms';
          if (panelYPercent.current > -70) {
            panelYPercent.current = 0;
            // Close the section
            // panelRef.current.style.transform = 'translateY(0)';
            panelRef.current.style.setProperty(
              '--panelYPercent',
              `max(-100%,${panelYPercent.current}%)`
            );
            toggleSection();
          } else {
            // Return the panel to its open position
            panelYPercent.current = 0;
            startY.current = 0;
            currentY.current = 0;
            // panelRef.current.style.transform = 'translateY(-100%)';
            panelRef.current.style.setProperty('--panelYPercent', '-100%');
          }
        }
      }
    };

    const toggleSection = useCallback(() => {
      let newPath: string | null;

      if (subSection) {
        // If this is a subsection and it exists in the path, we must be closing it, so remove it from the path
        newPath = !selectedPath?.includes(sectionKey)
          ? selectedPath + '/' + sectionKey
          : selectedPath.replace('/' + sectionKey, '');
      } else {
        // If this is a root section, we must be closing its subsections, so reset the path to just this section
        // otherwise empty it to close the tool palette
        newPath = selectedPath !== sectionKey && sectionKey !== null ? sectionKey : null;

        if (newPath === null) {
          // If we're going back to the root, make sure focus mode is turned off
          ui.dispatch(UserInterfaceActions.setViewOffset(0));
          ui.dispatch(UserInterfaceActions.setFocusMode(false));

          // Set focus on this section's root button
          setTimeout(() => {
            sectionButtonRef.current?.focus();
          }, 0);
        }
      }

      // When navigating via keyboard, the focus can get trapped in the section begin close
      // So we need to un focus the current element, then place focus in the next section after a delay
      (document.activeElement as HTMLElement | null)?.blur();

      if (newPath?.includes(sectionKey)) {
        isOpen.current = true;
      } else {
        isOpen.current = false;
      }

      ui.dispatch(UserInterfaceActions.setToolPaletteSection(newPath));

      if (onToggle) {
        onToggle(newPath as string, isOpen.current);
      }
    }, [subSection, selectedPath, sectionKey, ui, onToggle]);

    // Build the section classes

    const sectionClassNames = classNames(
      'flex',
      'flex-col',
      // If this section's button is hidden, override any margin that might be applied to it by the parent
      !showButton && '!m-0',
      props.className
    );

    // Build the base class names for the header area
    const headerClasses = classNames(
      'flex',
      'flex-col',
      'border-box',
      'overflow-hidden',
      'lg:mx-md',
      'lg:mb-md',
      'sm:justify-center',
      'sm:h-[40rem]',
      'sm:mb-sm',
      // Small screen drag-to-close handle
      'sm:after:absolute',
      'sm:after:self-center',
      'sm:after:h-[4rem]',
      'sm:after:w-xl',
      'sm:after:rounded-full',
      'sm:after:bg-themeTextColour20',
      'sm:after:top-[4rem]',
      //Large screen header separator
      showSeparator && [
        'lg:after:w-full',
        'lg:after:border-b',
        'lg:after:border-themeTextColour',
        'lg:after:opacity-20',
      ]
    );

    // Build the class names for the heading text
    const headingClassNames = classNames(
      'sm:mx-auto',
      'sm:justify-center',
      'sm:line-clamp',
      'sm:line-clamp-1',
      'sm:align-center',
      'sm:text-center',
      'sm:justify-center',
      'lg:flex',
      'lg:items-center',
      'lg:h-[60rem]',
      'lg:!mb-0',
      subSection && showCloseButton && 'lg:ml-[40rem]'
    );

    const paletteExperience = experience.EDITOR.toolPalette;

    const panelClassModifiers = [
      // ui.state.isSmallScreen ? 'mobile' : 'large',
      selected ? 'selected' : undefined,
      subSection ? 'sub-section' : 'root-section',
      paletteExperience.mobileSlideUpPanels === 'msuc' ? 'cover' : 'over',
      subSectionPanelType,
      smallScreenPanelHeight,
      toolPaletteStyle,
    ];

    const panelClassNames = classNames(
      'tpx-palette-section',
      // All modifier classes for the different types and states are in ToolPaletteSection.scss
      panelClassModifiers.map(modifier => `tpx-palette-section--${modifier}`)
    );

    const stackedButtonClasses = classNames(
      'px-xs',
      // On mobile screens above 300px wide, the tool palette buttons shrink to fit up to 5.5 on the screen.
      // Below 300px, they are fixed at 60px wide
      // Note that the buttons components are designed to allow multi word labels to wrap, but long words will never be broken and will extend the width of th ebutton
      'sm:min-w-[60px]',
      'sm:min300:min-w-[min(calc(var(--availableWidth)/5.5),100px)]'
    );

    let buttonProps: Partial<ButtonProps> = {};

    const tabButtonClasses = classNames(
      !subSection && 'h-[55rem]',
      !subSection && !selected && 'pl-[18rem]',
      !subSection && selected && 'ml-xs pl-[12rem] z-30'
    );

    const tabStyleButtonProps: Partial<ButtonProps> = {
      buttonStyle: buttonStyle ? buttonStyle : selected ? 'special' : 'standard',
      size: buttonSize,
      className: classNames(tabButtonClasses, buttonClasses),
      tabPosition: selected ? 'left' : undefined,
      hideLabel: selectedPath !== null ? true : false,
      startIcon: buttonIcon,
      labelLines: 2,
      stacked: ui.state.isSmallScreen,
      corners: selected ? 'theme' : 'square',
      labelAlignment: 'left',
      badgeContent: badgeContent ? badgeContent : undefined,
      badgePosition: badgeContent ? 'icon-left' : undefined,
      badgeStyle: badgeContent ? ThemeName.Notification : undefined,
    };

    const stackedStyleButtonProps: Partial<ButtonProps> = {
      buttonStyle: buttonStyle ? buttonStyle : 'standard',
      boxless: ui.state.isSmallScreen,
      size: 'small',
      className: classNames(stackedButtonClasses, buttonClasses),
      startIcon: buttonIcon,
      labelLines: 3,
      stacked: true,
    };

    const horizontalStyleButtonProps: Partial<ButtonProps> = {
      buttonStyle: buttonStyle ? buttonStyle : 'distinct',
      size: ui.state.isSmallScreen ? 'large' : 'medium',
      className: classNames(buttonClasses),
      labelAlignment: 'left',
      arrow: disabled ? false : true,
      startIcon: buttonIcon,
      labelLines: 2,
    };

    const iconOnlyButtonProps: Partial<ButtonProps> = {
      buttonStyle: buttonStyle ? buttonStyle : 'distinct',
      size: buttonSize,
      hideLabel: true,
      className: classNames(buttonClasses, 'self-start'),
      startIcon: buttonIcon,
    };

    switch (true) {
      // Large screen root sections:
      case buttonLayout === 'tab' ||
        (buttonLayout === 'auto' && !subSection && !ui.state.isSmallScreen):
        buttonProps = tabStyleButtonProps;
        break;
      // Small screen root sections or stacked style buttons
      case buttonLayout === 'stacked' || (buttonLayout === 'auto' && ui.state.isSmallScreen):
        buttonProps = stackedStyleButtonProps;
        break;
      case buttonLayout === 'horizontal' ||
        (buttonLayout === 'auto' && subSection && !ui.state.isSmallScreen):
        buttonProps = horizontalStyleButtonProps;
        break;
      case buttonLayout === 'icon-only':
        buttonProps = iconOnlyButtonProps;
        break;
    }

    const closeButtonDefaultClasses = classNames('tpx-palette-close-button');

    const closeButtonType1: Partial<ButtonProps> = {
      // Square, CloseIcon, Right aligned (Large screen root section)
      startIcon: <CloseIcon />,
      className: classNames('absolute top-md right-md', closeButtonDefaultClasses),
    };

    const closeButtonType2: Partial<ButtonProps> = {
      // Square, ArrowIcon, Left aligned (Large screen sub section)
      startIcon: <ChevronLeftIcon />,
      className: classNames('absolute top-md left-md', closeButtonDefaultClasses),
    };

    const closeButtonType3: Partial<ButtonProps> = {
      // Round, CloseIcon, Right aligned (Small screen root section or pop-up sub section)
      startIcon: <CloseIcon />,
      corners: 'round',
      className: classNames('absolute top-xs right-xs', closeButtonDefaultClasses),
    };

    const closeButtonType4: Partial<ButtonProps> = {
      // Round, ArrowIcon, Left aligned (Small screen slid-in sub section)
      startIcon: <ChevronLeftIcon />,
      corners: 'round',
      className: classNames('absolute top-xs left-xs', closeButtonDefaultClasses),
    };

    let closeButtonProps: Partial<ButtonProps> = {};

    switch (true) {
      case !ui.state.isSmallScreen && !subSection:
        closeButtonProps = closeButtonType1;
        break;
      case !ui.state.isSmallScreen && subSection:
        closeButtonProps = closeButtonType2;
        break;
      case ui.state.isSmallScreen && (!subSection || subSectionPanelType === 'pop-up'):
        closeButtonProps = closeButtonType3;
        break;
      case ui.state.isSmallScreen && subSectionPanelType === 'slide-in':
        closeButtonProps = closeButtonType4;
    }

    // Toggle or update focus mode when opening or returning to this section, or changing screen size
    useEffect(() => {
      const activeSection = ui.state.palette.section?.split('/').pop();
      isOpen.current = ui.state.palette.section?.includes(sectionKey) || false;
      if (isOpen.current) {
        panelRef.current?.style.setProperty('--panelYPercent', '-100%');
      } else {
        panelRef.current?.style.setProperty('--panelYPercent', '0%');
      }

      if (activeSection === sectionKey) {
        // Set isActiveSection to allow this section to be closed (prevents parent section being closed inadvertently by swipe)
        setisActiveSection(true);
        if (useFocusMode && ui.state.isSmallScreen) {
          // Set focus mode to the relevant position if the active section requires it
          ui.dispatch(UserInterfaceActions.setViewOffset(145));
          ui.dispatch(UserInterfaceActions.setFocusMode(true));
        } else {
          // Turn off focus mode if the active section does not require it
          ui.dispatch(UserInterfaceActions.setViewOffset(0));
          ui.dispatch(UserInterfaceActions.setFocusMode(false));
        }
      } else {
        setisActiveSection(false);
      }
    }, [ui.state.palette.section, ui.state.isSmallScreen]);

    useEffect(() => {
      if (subSection) {
        if (selected && paletteSectionRef.current) {
          // Add aria-hidden and inert="true" to all siblings when this section is open to prevent access via keyboard or screen readers
          const siblings = Array.from(paletteSectionRef.current.parentElement?.children || []);
          siblings.forEach(sibling => {
            if (sibling !== paletteSectionRef.current) {
              sibling.setAttribute('aria-hidden', 'true');
              sibling.setAttribute('inert', 'true');
              sibling.setAttribute('tabindex', '-1');
            }
          });
        } else if (wasSelected.current && !selected && paletteSectionRef.current) {
          // Remove the attributes when closing the palette
          const siblings = Array.from(paletteSectionRef.current.parentElement?.children || []);
          siblings.forEach(sibling => {
            if (sibling !== paletteSectionRef.current) {
              sibling.removeAttribute('aria-hidden');
              sibling.removeAttribute('inert');
              sibling.removeAttribute('tabindex');
            }
          });
        }
      }

      wasSelected.current = selected;
    }, [selected, paletteSectionRef.current]);

    return (
      <section
        className={sectionClassNames}
        {...sectionAttributes}
        ref={paletteSectionRef}
        data-section-palette
      >
        {showButton && (
          <Button
            {...buttonProps}
            id={`toolPaletteButton-${props.id}`}
            label={buttonLabel ? buttonLabel : title}
            onClick={toggleSection}
            tabIndex={enabledIfParentSectionselected}
            aria-controls="unique-tab-id"
            aria-selected={selected}
            ref={sectionButtonRef}
            disabled={disabled}
            data-palette-button
            inert={subSection && selected ? 'true' : undefined} // Subsection buttons are covered when the section is open, so it must be inert
            aria-hidden={subSection && selected ? 'true' : undefined} // Subsection buttons are covered when the section is open, so it must be hidden to screen readers
          />
        )}
        {/* The tpx-theme-main element ensure that the toolpalette content inherits from the main theme rather than the toolpaletteOpen theme */}
        <Theme
          name={'tpx-theme-main'}
          tipLabel={'Base'}
          isRootTheme
          allowBorder={false}
          allowCorners={false}
          allowBackground={false}
          data-palette-content
          // Prevent this section from being focusable when it is not visible:
          inert={!selected ? 'true' : undefined}
          // Prevent this section from being focusable when it is not visible:
          aria-hidden={!selected ? 'true' : undefined}
        >
          <Theme
            name={'tpx-theme-toolpaletteContent'}
            tipLabel={'Sidebar (Content)'}
            isRootTheme
            id={props.id}
            aria-label={`${title} tool palette`}
            aria-hidden={ariaHidden}
            className={panelClassNames}
            allowCorners={toolPaletteStyle !== 'docked'}
            allowBorder={toolPaletteStyle !== 'docked'}
            ref={panelRef}
          >
            <div
              className={headerClasses}
              // Add the touch handlers to allow swipe to close
              onTouchStart={ui.state.isSmallScreen ? handleTouchStart : undefined}
              onTouchMove={ui.state.isSmallScreen ? handleTouchMove : undefined}
              onTouchEnd={ui.state.isSmallScreen ? handleTouchEnd : undefined}
            >
              <Heading
                level={1}
                size={ui.state.isSmallScreen ? 5 : 2}
                className={headingClassNames}
              >
                {headingText}
              </Heading>
              {showCloseButton && (
                <>
                  <Button
                    label={t('toolpalette:str_LabelClosePanel')}
                    {...closeButtonProps}
                    buttonStyle={'tertiary'}
                    size="small"
                    hideLabel={true}
                    onClick={e => {
                      if (isActiveSection) {
                        e.stopPropagation();
                        if (customCloseFunction) {
                          customCloseFunction();
                        } else {
                          toggleSection();
                        }
                      }
                    }}
                    onTouchStart={e => e.stopPropagation()}
                    onTouchEnd={e => e.stopPropagation()}
                    tabIndex={enabledIfThisSectionselected}
                    // If any subsections are open, make sure this button is not accessible via keyboard:
                    inert={!subSection && selected && hasOpenSubsection ? 'true' : undefined}
                    // If any subsections are open, make sure this button is not accessible via screen readers:
                    aria-hidden={!subSection && selected && hasOpenSubsection ? 'true' : undefined}
                  />
                </>
              )}
            </div>
            {React.Children.map(
              children,
              (child: React.ReactElement, index: number): React.ReactElement => {
                return React.cloneElement(child, {
                  key: index,
                  parentselected: {selected},
                });
              }
            )}
          </Theme>
        </Theme>
      </section>
    );
  }
);

export default ToolPaletteSection;
